import { useState, useEffect, useMemo } from "react";
import {
    TablePagination,
    Button,
    Box,
    FormControl,
    InputAdornment,
    OutlinedInput,
    Typography,
    InputLabel,
    Select,
    MenuItem,
} from "@mui/material";
import Table from "../../components/Table";
import ActionsMenu from "../../components/ActionsMenu";
import CourseModal from "./components/CourseModal";
import CourseAssignmentsModal from "./components/CourseAssignmentsModal";
import { CourseColumns } from "./components/CourseColumns";
import AddIcon from '@mui/icons-material/Add';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import api from "../../utils/api";

export default function Courses() {
    const columns = useMemo(() => CourseColumns(), []);

    const [search, setSearch] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("");
    const [categories, setCategories] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, perPage: 40, total: 0, totalPages: 0 });
    const [activeFilters, setActiveFilters] = useState({});
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [row, setRow] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [assignmentsCourse, setAssignmentsCourse] = useState(null);
    const [isAssignmentsOpen, setIsAssignmentsOpen] = useState(false);

    useEffect(() => {
        api.get("admin/categories/list").then((res) => {
            setCategories(res.data.data || []);
        }).catch(() => setCategories([]));
    }, []);

    const fetchRows = async (filters = activeFilters, options = {}) => {
        const { resetPage = false, overridePagination = {} } = options;
        const currentPage = resetPage ? 1 : overridePagination.page ?? pagination.page;
        const currentPerPage = overridePagination.perPage ?? pagination.perPage;
        setLoading(true);
        try {
            const response = await api.get("admin/courses", {
                params: {
                    filter: filters,
                    page: currentPage,
                    perPage: currentPerPage,
                },
            });
            setPagination({
                page: currentPage,
                perPage: currentPerPage,
                total: response.data.meta.pagination.total,
                totalPages: response.data.meta.pagination.totalPages,
            });
            setRows(response.data.data);
        } catch (error) {
            console.error("Fetch failed", error);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteRows = async (rowsToDelete) => {
        try {
            for (const id of rowsToDelete) {
                await api.delete(`admin/courses/${id}`);
            }
            fetchRows(activeFilters);
        } catch (err) {
            console.error(err);
            const msg =
                err.errors?.course?.[0] ||
                err.response?.data?.errors?.course?.[0] ||
                err.response?.data?.message ||
                "Failed to delete course.";
            window.alert(msg);
        }
    };

    const handleOpenManageRow = (r) => {
        setRow(r ?? null);
        setIsModalOpen(true);
    };

    const handleCloseManageRow = (refetch, createdCourse = null) => {
        setRow(null);
        setIsModalOpen(false);
        if (refetch) fetchRows(activeFilters);
        if (createdCourse && createdCourse.id) {
            setAssignmentsCourse(createdCourse);
            setIsAssignmentsOpen(true);
        }
    };

    const handlePageChange = (newPage) => {
        fetchRows(activeFilters, { overridePagination: { page: newPage + 1 } });
    };

    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            const newFilters = { name: search };
            if (categoryFilter !== '') newFilters.category_id = categoryFilter;
            setActiveFilters(newFilters);
            fetchRows(newFilters, { resetPage: true });
        }, 500);
        return () => clearTimeout(delayDebounce);
    }, [search, categoryFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    const getCourseName = (r) => r?.name_en || r?.name_ru || r?.name_hy || 'Course';

    return (
        <Box>
            <Typography component="h2" variant="h6" sx={{ mb: 2 }}>
                Courses
            </Typography>
            <Box>
                <Box sx={{ display: "flex", mb: 2, justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
                        <FormControl sx={{ width: { xs: '100%', md: '32ch' } }} variant="outlined" size="small">
                            <OutlinedInput
                                size="small"
                                placeholder="Search by name..."
                                sx={{ background: "#fff" }}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                startAdornment={
                                    <InputAdornment position="start">
                                        <SearchRoundedIcon fontSize="small" />
                                    </InputAdornment>
                                }
                            />
                        </FormControl>
                        <FormControl sx={{ minWidth: 160 }} size="small">
                            <InputLabel>Category</InputLabel>
                            <Select
                                value={categoryFilter}
                                label="Category"
                                onChange={(e) => setCategoryFilter(e.target.value)}
                            >
                                <MenuItem value="">All</MenuItem>
                                {categories.map((cat) => (
                                    <MenuItem key={cat.id} value={cat.id}>
                                        {cat.title_en || cat.title_ru || cat.title_hy || cat.id}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenManageRow()}
                    >
                        Create New
                    </Button>
                </Box>

                <Table
                    loading={loading}
                    rows={rows}
                    columns={columns}
                    selactable={false}
                    draggable={false}
                    renderActions={(r) => (
                        <ActionsMenu
                            actions={[
                                { label: "Edit", onClick: () => handleOpenManageRow(r) },
                                { label: "Assignments", onClick: () => { setAssignmentsCourse(r); setIsAssignmentsOpen(true); } },
                                {
                                    label: "Delete",
                                    onClick: () => handleDeleteRows([r.id]),
                                    confirm: true,
                                    confirmText: `Are you sure you want to delete "${getCourseName(r)}"?`,
                                },
                            ]}
                        />
                    )}
                />

                <TablePagination
                    component="div"
                    count={pagination.total}
                    page={pagination.page - 1}
                    rowsPerPage={pagination.perPage}
                    onPageChange={(e, newPage) => handlePageChange(newPage)}
                    rowsPerPageOptions={[]}
                />
            </Box>
            <CourseModal row={row} open={isModalOpen} onClose={handleCloseManageRow} />
            <CourseAssignmentsModal
                open={isAssignmentsOpen}
                course={assignmentsCourse}
                onClose={() => {
                    setIsAssignmentsOpen(false);
                    setAssignmentsCourse(null);
                }}
            />
        </Box>
    );
}
